import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Loader2, Phone } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import {
  getActivationStatus,
  sendPhoneOtp,
  verifyPhoneOtp,
} from '../../api/creator_onboarding';

/**
 * Step 2 · Phone OTP verification (SMS). Terms acceptance and activation
 * live in Step 3 (StepActivation).
 */
const StepPhoneVerify = ({ prefillPhone = '', onVerifiedChange }) => {
  const { t } = useTranslation('onboarding');
  const { toast } = useToast();

  const [statusLoading, setStatusLoading] = useState(true);
  const [phone, setPhone] = useState(prefillPhone || '');
  const [code, setCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [isMock, setIsMock] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [verifiedPhone, setVerifiedPhone] = useState('');
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const s = await getActivationStatus();
        setPhoneVerified(!!s.phone_verified);
        setVerifiedPhone(s.phone || '');
        setIsMock(!!s.sms_mock);
        if (s.phone && !prefillPhone) setPhone(s.phone);
        onVerifiedChange?.(!!s.phone_verified);
      } catch (_e) { /* soft */ } finally {
        setStatusLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const errMsg = (e, fallback) => {
    const d = e?.response?.data?.detail;
    if (typeof d === 'string') return d.includes('|') ? d.split('|')[1] : d;
    return fallback;
  };

  const handleSendOtp = async () => {
    setBusy('send');
    try {
      const res = await sendPhoneOtp(phone);
      setOtpSent(true);
      if (res.mock_code) {
        setCode(res.mock_code);
        toast({ title: t('phone_step.mock_hint'), description: res.mock_code });
      } else {
        toast({ title: t('phone_step.otp_sent_title'), description: t('phone_step.otp_sent_desc') });
      }
    } catch (e) {
      toast({ title: t('phone_step.otp_error'), description: errMsg(e, 'Riprova'), variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const handleVerify = async () => {
    setBusy('verify');
    try {
      const res = await verifyPhoneOtp(phone, code);
      setPhoneVerified(true);
      setVerifiedPhone(res.phone || phone);
      onVerifiedChange?.(true);
      toast({ title: t('phone_step.verified_title') });
    } catch (e) {
      toast({ title: t('phone_step.verify_error'), description: errMsg(e, 'Riprova'), variant: 'destructive' });
    } finally { setBusy(null); }
  };

  if (statusLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> …
      </div>
    );
  }

  return (
    <div data-testid="wizard-step-phone">
      <h2 className="text-xl font-semibold text-foreground">{t('phone_step.title')}</h2>
      <p className="mt-1 mb-5 text-sm text-muted-foreground">{t('phone_step.subtitle')}</p>

      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <div className="flex items-center gap-2">
          <Phone className="h-4 w-4 gold-text" />
          <p className="text-sm font-semibold text-foreground">{t('phone_step.phone_block_title')}</p>
          {phoneVerified && (
            <span
              className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-300"
              data-testid="phone-verified-badge"
            >
              <CheckCircle2 className="h-3 w-3" /> {t('phone_step.verified_badge')}
            </span>
          )}
        </div>

        {phoneVerified ? (
          <p className="mt-2 text-sm text-muted-foreground" data-testid="phone-verified-number">
            {t('phone_step.verified_number', { phone: verifiedPhone })}
          </p>
        ) : (
          <>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <Input
                type="tel"
                inputMode="tel"
                placeholder={t('phone_step.phone_placeholder')}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="sm:max-w-[260px]"
                data-testid="phone-input"
              />
              <Button
                type="button"
                onClick={handleSendOtp}
                disabled={busy === 'send' || phone.trim().length < 7}
                variant="outline"
                className="border-amber-500/40 gold-text hover:bg-amber-500/10"
                data-testid="send-otp-btn"
              >
                {busy === 'send'
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : (otpSent ? t('phone_step.resend_code') : t('phone_step.send_code'))}
              </Button>
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">{t('phone_step.phone_format_hint')}</p>

            {otpSent && (
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder={t('phone_step.code_placeholder')}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className="tracking-[0.3em] sm:max-w-[180px]"
                  data-testid="otp-code-input"
                />
                <Button
                  type="button"
                  onClick={handleVerify}
                  disabled={busy === 'verify' || code.length < 4}
                  className="gold-gradient text-black"
                  data-testid="verify-otp-btn"
                >
                  {busy === 'verify' ? <Loader2 className="h-4 w-4 animate-spin" /> : t('phone_step.verify_code')}
                </Button>
              </div>
            )}
            {otpSent && isMock && (
              <p className="mt-1.5 text-[11px] italic text-muted-foreground" data-testid="otp-mock-note">
                {t('phone_step.mock_note')}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StepPhoneVerify;
